<template>
  <div class="iframes open-iframe">
    <div class="iframes__details">
      <div class="toggle-icons">
        <div class="toggle-icons__mark-as-finish">
          <span class="toggle-icons__label">MARK FINISHED</span>
          <SwitchCheckbox
            v-model="isFinished"
            :disabled="isLoading || isDelayed"
          ></SwitchCheckbox>
          <LoadingSmallSpinner v-if="isLoading" />
        </div>
      </div>
      <div
        v-if="currentParent.type"
        class="iframes__report-issue"
        @click.stop="startIssue"
      >
        <span class="iframes__report-issue__text">Report an issue</span>
      </div>
      <div
        v-if="supportFullScreen"
        class="fullscreen pointer"
        @click="toggleFullscreen()"
      >
        <FullscreenIcon class="fullscreen__icon" />
      </div>
    </div>
    <div class="iframes__player-wrapper">
      <iframe
        v-if="iframeURL"
        ref="iframe"
        class="iframes__window"
        :src="iframeURL"
        frameborder="0"
        marginheight="0"
        marginwidth="0"
      ></iframe>
      <div
        v-if="content.files && content.files.length"
        class="iframes__attachments"
      >
        <span class="iframes__attachments-label">Slides attachment(s):</span>
        <Files
          class="iframe-files"
          :content="content"
          :canOpen="false"
          :transparent="true"
        />
      </div>
    </div>
  </div>
</template>

<script>
import FullscreenIcon from '@/assets/icons/course/iframe/fullscreen-icon.svg'
import Files from '@/components/modals/exercise-modal/Files.vue'
import CONSTANTS from '@/constants'
import SwitchCheckbox from '@/components/forms/elements/SwitchCheckbox.vue'
import LoadingSmallSpinner from '@/components/loaders/LoadingSmallSpinner.vue'
import { getDarklyFlagKeyValue } from '@/utils'
import { mapState } from 'vuex'

export default {
  name: 'IFrame',
  components: {
    FullscreenIcon: { ...FullscreenIcon, name: 'FullscreenIcon' },
    Files,
    SwitchCheckbox,
    LoadingSmallSpinner,
  },
  props: {
    content: {
      type: Object,
      required: true,
    },
    userCompletionStatus: {
      type: String,
      required: true,
    },
    parentType: {
      type: String,
      required: false,
      default: () => 'isolated',
    },
    parent: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      isLoading: false,
      isDelayed: false,
      iframeURL: '',
    }
  },
  computed: {
    ...mapState({
      currentParent: state => state.content.currentParent,
    }),
    hasWatched() {
      return (
        this.userCompletionStatus === CONSTANTS.CONTENT_USER_STATUS.FINISHED
      )
    },
    hasStarted() {
      return (
        this.userCompletionStatus === CONSTANTS.CONTENT_USER_STATUS.STARTED ||
        this.userCompletionStatus === CONSTANTS.CONTENT_USER_STATUS.FINISHED
      )
    },
    isFinished: {
      get() {
        return this.hasWatched
      },
      set() {
        this.toggleIFrameStatus()
      },
    },
  },
  async created() {
    this.$emit('is-loading', true)
    const response = await this.$store.dispatch(
      'iframes/getSignedIFrameURL',
      this.content.id
    )
    if (!this.hasStarted) {
      await this.updateIFrameStatus(CONSTANTS.CONTENT_USER_STATUS.STARTED)
      if (this.parentType === 'course') {
        this.sendMixpanelEvent(this.eventsConstants.CONTENT_IFRAME_STARTED, {
          course_id: this.parent.id,
          course_name: this.parent.name,
          iframe_id: this.content.id,
          iframe_name: this.content.name,
          ...this.content,
        })
      }
    }
    this.iframeURL = response.url
    this.$emit('is-loading', false)
    if (this.parentType === 'course') {
      this.sendMixpanelEvent(
        this.eventsConstants.CONTENT_IFRAME_MEDIA_REQUESTED,
        {
          course_id: this.parent.id,
          course_name: this.parent.name,
          iframe_id: this.content.id,
          iframe_name: this.content.name,
          ...this.content,
        }
      )
    }
    document.addEventListener('fullscreenchange', this.scrollToOpenIframe)
  },
  beforeDestroy() {
    document.removeEventListener('fullscreenchange', this.scrollToOpenIframe)
  },
  mounted() {
    this.$scrollTo('.open-iframe', 1000, {
      container: 'body',
      offset: -220,
    })
    setTimeout(() => {
      this.$emit('is-course-active')
    }, 15000)
  },
  methods: {
    toggleFullscreen() {
      if (!this.fullScreenStatus()) {
        this.launchIntoFullscreen(this.$refs.iframe)
      } else {
        this.exitFullscreen()
      }
    },
    launchIntoFullscreen(element) {
      if (element.requestFullscreen) {
        element.requestFullscreen()
      } else if (element.mozRequestFullScreen) {
        element.mozRequestFullScreen()
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen()
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen()
      }
    },
    exitFullscreen() {
      if (document.exitFullscreen) {
        document.exitFullscreen()
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen()
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen()
      }
    },
    fullScreenStatus() {
      if (
        document.mozFullScreen ||
        document.fullscreenElement ||
        document.msFullscreenElement ||
        document.webkitIsFullScreen
      ) {
        return true
      }
      return false
    },
    supportFullScreen() {
      const doc = document.documentElement

      return (
        'requestFullscreen' in doc ||
        ('mozRequestFullScreen' in doc && document.mozFullScreenEnabled) ||
        ('msRequestFullscreen' in doc && document.msFullscreenEnabled) ||
        'webkitRequestFullScreen' in doc
      )
    },
    scrollToOpenIframe() {
      if (!this.fullScreenStatus()) {
        setTimeout(() => {
          this.$scrollTo('.open-iframe', 10, {
            container: 'body',
            offset: -220,
          })
        }, 10)
      }
    },
    async updateIFrameStatus(status) {
      this.isLoading = true
      await this.$store.dispatch('content/updateIFrameStatus', {
        iframeId: this.content.id,
        status,
      })
      this.isLoading = false
    },
    async toggleIFrameStatus() {
      const isSetStatusDelayed = await getDarklyFlagKeyValue(
        'setContentStatusDelayActive'
      )
      const updatedStatus =
        this.userCompletionStatus === CONSTANTS.CONTENT_USER_STATUS.FINISHED
          ? CONSTANTS.CONTENT_USER_STATUS.UNSTARTED
          : CONSTANTS.CONTENT_USER_STATUS.FINISHED

      if (isSetStatusDelayed) {
        this.isDelayed = true
        setTimeout(() => {
          this.isDelayed = false
        }, process.env.VUE_APP_SET_CONTENT_STATUS_DELAY)
      }

      await this.updateIFrameStatus(updatedStatus)

      if (
        this.parentType === 'course' &&
        updatedStatus === CONSTANTS.CONTENT_USER_STATUS.FINISHED
      ) {
        this.sendMixpanelEvent(this.eventsConstants.CONTENT_IFRAME_FINISHED, {
          course_id: this.parent.id,
          course_name: this.parent.name,
          iframe_id: this.content.id,
          iframe_name: this.content.name,
          ...this.content,
        })
        setTimeout(async () => {
          await this.$store.dispatch('courses/getCourseById', this.parent.id)
        }, 500)
      }
    },
    startIssue() {
      this.$store.dispatch('content/saveContentIssue', {
        content_type: 'iframe',
        content_subtype: this.content.type,
        content_name: this.content.name,
        content_id: this.content.id,
        parent_id: this.currentParent.id,
        parent_type: this.currentParent.type,
        extra_args: {
          iframe_cdn_url: this.iframeURL,
        },
      })
    },
  },
}
</script>

<style lang="scss">
.iframes {
  $this: &;
  margin: 0 auto 50px 0;
  transition: 1s all ease;
  position: relative;
  &__details {
    display: flex;
    align-items: center;
    margin: 20px 0 20px 0;
    flex-wrap: wrap;
    @include mobile {
      flex-wrap: nowrap;
    }
  }
  &__icon-wrapper {
    background: var(--light-gray);
    height: 62px;
    width: 62px;
    margin: 0 5px 0 0;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &__loading-wrapper {
    width: 62px;
    height: 62px;
    text-align: center;
  }
  &__loading-spinner {
    margin: 5px 0 0 0;
  }
  &__icon {
    vertical-align: middle;
  }
  &__length-wrapper {
    display: flex;
    align-items: center;
    margin: 5px 0 0 50px;
  }
  &__length {
    color: var(--white);
  }
  &__player-wrapper {
    @include desktop {
      padding: 0 50px;
    }
    width: 100%;
  }
  &__window {
    width: 100%;
    min-height: 80vh;
    background: rgb(214, 214, 214);
    @include lg-desktop {
      min-height: 700px;
    }
  }
  .pointer {
    cursor: pointer;
  }
  .fullscreen {
    @include mobile {
      margin-right: 30px;
    }
    color: var(--light-blue-gray);
    display: flex;
    align-content: center;
    &__text {
      padding-left: 10px;
    }

    &__icon {
      stroke-width: 1;
    }

    width: 36px;
    height: 36px;
    align-items: center;
    justify-content: center;

    &:hover {
      background: #ffffff;
      color: var(--charcoal);
      box-shadow: 0px 4px 40px rgba(255, 255, 255, 0.4);
      border-radius: 5px;
    }

    &:active {
      background: var(--athens-gray);
      color: var(--charcoal);
      box-shadow: 0px 4px 40px rgba(255, 255, 255, 0.4);
      border-radius: 5px;
    }
  }
  &__icon-wrapper {
    height: 62px;
    margin: 0 5px 0 0;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .toggle-icons {
    width: 100%;
    justify-content: space-between;
    @include mobile {
      width: 250px;
      justify-content: initial;
    }
    &__check-wrapper {
      display: flex;
    }
    &__check {
      margin-left: 20px;
    }
    &__label {
      line-height: 0;
    }
    &__mark-as-finish {
      display: flex;
      align-items: center;
      background: transparent;
      color: var(--white);
      font-weight: 700;
      border: none;
      padding: 0px;
      margin: 0 0 0 10px;
      cursor: pointer;
    }
    .btn__switch {
      margin: 0px 10px;
    }
  }
  &__report-issue {
    color: var(--light-blue-gray);
    font-weight: 400;
    text-decoration: underline;
    margin-left: auto;
    margin-right: 50px;
    display: flex;
    align-items: center;
    cursor: pointer;
    &__icon {
      fill: #717171;
      margin-right: 5px;
    }
  }

  &__attachments {
    color: var(--white);
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding-top: 20px;

    @include tablet {
      padding: 20px;
    }
  }

  &__attachments-label {
    margin: 5px 0px;
  }

  .iframe-files {
    flex: none;
    order: 2;
    align-self: stretch;
    flex-grow: 0;
    margin: 5px 0px;
    padding: 0px;

    .file {
      $file: &;
      background: var(--glass-mid-gray);
      position: static;
      height: 52px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;

      @include tablet {
        padding: 8px 15px;
      }

      &:hover {
        background: linear-gradient(
          112.2deg,
          rgba(187, 188, 199, 0.4) -46.39%,
          rgba(187, 188, 199, 0) 112.38%
        );
        box-shadow: 0px 0px 1px rgba(255, 255, 255, 0.5);
      }
    }

    .accordion {
      flex: none;
      order: 1;
      align-self: stretch;
      flex-grow: 0;
      margin: 5px 0px;

      &:hover {
        .file {
          &__header {
            &-download-icon {
              background: #ffffff;
              box-shadow: 0px 4px 40px rgba(255, 255, 255, 0.4);
              border-radius: 5px;
              color: #1e1e1e;
            }

            &-status {
              &-wrapper {
                background: var(--white);
                box-shadow: 0px 4px 40px rgba(255, 255, 255, 0.4);
              }
            }
          }
        }
      }
    }
  }
}
</style>
